import React, { useEffect, useState } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Grid,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  filters,
  registerFilter,
  registeringFilter,
  registeringFilterDone,
  registering,
  reloadFilters,
} from 'features/filtersState';
import { FilterData, AlertState } from 'types/types';
import LocalForageService from 'services/LocalForageService';
import { dashboards } from 'features/dashboardsState';
import { RootState } from 'types/types';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DateRangeComponent from './DateRangeComponent';
import { showAlert } from 'features/applicationState';

interface FilterComponentProps {
  dashboardId: string;
  filter: FilterData;
  podId?: string;
}
interface FilterValue {
  filterId?: string;
  values: any[];
  contextInfo?: any;
  items?: any;
  selected?: boolean;
}
interface FilterValues {
  id?: string;
  name: string;
  contextInfo?: any;
  items?: any;
  selected?: boolean;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const FilterComponent: React.FC<FilterComponentProps> = ({
  dashboardId,
  filter,
}) => {
  const dashboardsState =
    useSelector((state: RootState) => dashboards(state)) || [];
  const dispatch = useDispatch();
  const filtersState = useSelector(filters);
  const registeringFilters = useSelector(registering);
  const [options, setOptions] = useState<FilterValues[]>([]);
  const [defaultValues, setDefaultValues] = useState<FilterValues[]>([]);

  let newFilter = { ...filter };
  const registeredFilter = filtersState.find(
    (f) =>
      f.filterId === newFilter.filterId &&
      f.dashboardId === dashboardId &&
      f.loaded
  );
  const registeredFilterNotLoaded = filtersState.find(
    (f) =>
      f.filterId === newFilter.filterId &&
      f.dashboardId === dashboardId &&
      !f.loaded
  );

  if (registeredFilter) {
    newFilter = { ...registeredFilter };
  }

  const getSelectedValues = (
    filter: FilterData,
    notLoadedFilter?: FilterData
  ) => {
    if (notLoadedFilter && notLoadedFilter.selectedValues) {
      const remainingFilters = notLoadedFilter.selectedValues.filter((value) =>
        filter.values?.some((v) => v.id === value.id)
      );
      remainingFilters.forEach((value) => {
        const foundValue = filter.values?.find((v) => v.id === value.id);
        if (foundValue) foundValue.selected = true;
      });
      return remainingFilters.length === 0 &&
        filter.values &&
        filter.values.length > 0
        ? [{ ...filter.values[0], selected: true }]
        : remainingFilters;
    }
    return filter.values ? [{ ...filter.values[0], selected: true }] : [];
  };

  const loadFilterValues = async (filter: FilterData) => {
    const filterValues = filter.cascadeParentFilters?.map((parentFilterId) => {
        const parentFilter = filtersState.find(
          (f) => f.filterId === parentFilterId && f.dashboardId === dashboardId
        );
        return parentFilter &&
          parentFilter.selectedValues &&
          parentFilter.selectedValues.length > 0 &&
          parentFilter.loaded
          ? { filterId: parentFilterId, values: parentFilter.selectedValues }
          : null;
      })
      .filter(Boolean) as FilterValue[];

    if (filterValues.length === filter.cascadeParentFilters?.length) {
      const filterRequest = {
        dashboardId,
        userId: '',
        podId: '',
        filterId: newFilter.filterId,
        filters: filterValues,
      };
      const lsService = new LocalForageService();
      dispatch(registeringFilter(newFilter.filterId));
      const filterData = await lsService.handleFilterData(filterRequest, false);
      newFilter.values = filterData.values;
      newFilter.selectedValues = getSelectedValues(
        newFilter,
        registeredFilterNotLoaded
      );
      dispatch(
        registerFilter({ dashboardId, filter: newFilter, loaded: true })
      );
      dispatch(registeringFilterDone(newFilter.filterId));
    }
  };

  const setSelectedFlag = (
    values: FilterValues[],
    selectedValues: FilterValues[]
  ) => {
    const selectedSet = new Set(selectedValues.map((val) => val.id));
    return values.map((value) => ({
      ...value,
      selected: selectedSet.has(value.id),
    }));
  };

  useEffect(() => {
    if (!registeredFilter) {
      if (newFilter.category) {
        if (newFilter.selectionType === 'singleSelection') {
          newFilter.selectedValues =
            newFilter && newFilter.values ? [newFilter.values[0]] : [];
        } else {
          newFilter.selectedValues = newFilter.values;
        }
        dispatch(
          registerFilter({ dashboardId, filter: newFilter, loaded: true })
        );
      } else if (newFilter.group) {
        newFilter.selectedValues = newFilter.values;
        dispatch(
          registerFilter({ dashboardId, filter: newFilter, loaded: true })
        );
      } else {
        if (newFilter.sourceType === 'localSource') {
          newFilter.selectedValues = getSelectedValues(
            newFilter,
            registeredFilterNotLoaded
          );
          dispatch(
            registerFilter({ dashboardId, filter: newFilter, loaded: true })
          );
        } else if (
          newFilter.sourceType === 'serverSource' &&
          newFilter.cascadeParentFilters
        ) {
          const allLoaded = newFilter.cascadeParentFilters.every(
            (parentFilterId) =>
              filtersState.some(
                (f) =>
                  f.filterId === parentFilterId &&
                  f.dashboardId === dashboardId &&
                  f.loaded
              )
          );
          if (
            allLoaded &&
            registeringFilters.indexOf(newFilter.filterId) === -1
          ) {
            loadFilterValues(newFilter);
          }
        } else if (newFilter.type === 'DashboardDescriptorFilterDateRange') {
          newFilter.selectedValues = [
            {
              id: `${new Date('2023-04-01').toISOString()}|${new Date(
                '2023-04-30'
              ).toISOString()}`,
              name: `${new Date('2023-04-01').toString()} to ${new Date(
                '2023-04-30'
              ).toString()}`,
            },
            { id: 'daily', name: 'daily' },
          ];
          dispatch(
            registerFilter({ dashboardId, filter: newFilter, loaded: true })
          );
        }
      }
    } else if (registeredFilter.values && registeredFilter.selectedValues) {
      const updatedValues = setSelectedFlag(
        registeredFilter.values,
        registeredFilter.selectedValues
      );
      setOptions(updatedValues);
      setDefaultValues(updatedValues.filter((value) => value.selected));
    }
    // eslint-disable-next-line
  }, [filtersState, dashboardId]);

  const handleMaxNumberOfSelectedValuesAllowed = (value: string) => {
    const selectedOptions = options.filter((el) => el.selected == true);
    const isOptionAlreadySelected = options.find((el) => el.name === value);

    let allowSelection = true;

    if (
      isOptionAlreadySelected &&
      !isOptionAlreadySelected.selected &&
      filter.maxSelectedValues &&
      selectedOptions.length >= filter.maxSelectedValues
    ) {
      const alertData: AlertState = {
        severity: 'warning',
        title: filter.maxSelectedValuesAlertMessage ?? '',
        message: filter.maxSelectedValuesAlertMessage ?? '',
        open: true,
      };
      dispatch(showAlert(alertData));
      allowSelection = false;
    }
    return allowSelection;
  };

  const handleSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: FilterValues[],
    type: 'checkbox' | 'radio'
  ) => {
    let updatedValues: FilterValues[];

    if (type === 'checkbox') {
      const checkboxValue = event.target.name;
      const allowSelection =
        handleMaxNumberOfSelectedValuesAllowed(checkboxValue);

      if (!allowSelection) {
        return;
      }
      updatedValues = options.map((option) =>
        option.name === checkboxValue
          ? { ...option, selected: !option.selected }
          : option
      );
    } else if (type === 'radio') {
      const radioValue = event.target.value;
      updatedValues = options.map((option) =>
        option.id === radioValue
          ? { ...option, selected: true }
          : { ...option, selected: false }
      );
    } else {
      const selectedValue = event.target.name;

      const allowSelection =
        handleMaxNumberOfSelectedValuesAllowed(selectedValue);

      if (!allowSelection) {
        return;
      }
      updatedValues = value;
    }

    setOptions(updatedValues);
    setDefaultValues(updatedValues.filter((value) => value.selected));

    const affectedFilters = new Set<string>();
    if (newFilter.linkedPodIds) {
      newFilter.linkedPodIds.forEach((pId) => {
        const dashboard = dashboardsState.find(
          (d) => d.dashboardId === dashboardId
        );
        if (!dashboard) return;

        const { pods, filters } = dashboard.descriptor;
        filters.forEach((f) => {
          if (f.cascadeParentFilters?.includes(newFilter.filterId)) {
            affectedFilters.add(f.filterId);
            f.linkedPodIds?.forEach((linkedPod) => {
              const pod = pods.find((p) => p.podId === linkedPod);
              pod?.filters?.forEach((filter) =>
                affectedFilters.add(filter.filterId)
              );
            });
          }
        });

        pods
          .filter((p) => p.podId === pId)
          .forEach((pod) => {
            pod.filters?.forEach((filter) => {
              if (filter.cascadeParentFilters?.includes(newFilter.filterId)) {
                affectedFilters.add(filter.filterId);
              }
            });
          });
      });
    }

    dispatch(
      reloadFilters({
        affectedFilters: Array.from(affectedFilters),
        filterId: newFilter.filterId,
        dashboardId,
        selectedValues: updatedValues.filter((value) => value.selected),
        loaded: false,
      })
    );
  };

  return (
    <Box className="filter-container" sx={{ p: 0, m: 0 }}>
      <Box className="filter-label">
        <strong>{registeredFilter?.label}</strong>
      </Box>
      {registeredFilter?.values && registeredFilter.values.length > 0 && (
        <Grid flexGrow={1}>
          {registeredFilter.selectionType === 'multipleSelection' ? (
            <FormGroup className="filter-entity-main">
              <Box className="filter-label">
                {registeredFilter.filterId.replace('_', ' ')}
              </Box>
              <Box className="options-selection">
                {registeredFilter.values.map((option) => (
                  <FormControlLabel
                    key={option.id}
                    control={
                      <Checkbox
                        checked={defaultValues.some(
                          (val) => val.id === option.id
                        )}
                        onChange={(e) =>
                          handleSelectionChange(e, [], 'checkbox')
                        }
                        name={option.name}
                      />
                    }
                    label={option.name}
                  />
                ))}
              </Box>
            </FormGroup>
          ) : registeredFilter.selectionType === 'singleSelection' ? (
            <FormControl component="fieldset" className="filter-entity-main">
              <Box className="filter-label">
                {registeredFilter.filterId.replace('_', ' ')}
              </Box>
              <RadioGroup
                value={defaultValues[0]?.id || ''}
                onChange={(e) => handleSelectionChange(e, [], 'radio')}
                className="options-selection"
              >
                {registeredFilter.values.map((option) => (
                  <FormControlLabel
                    key={option.id}
                    value={option.id}
                    control={<Radio />}
                    label={option.name}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          ) : (
            <Autocomplete
              multiple
              limitTags={2}
              size="small"
              id="multiple-limit-tags"
              options={options}
              getOptionLabel={(option) => option.name}
              onChange={(event, value) =>
                handleSelectionChange(event as any, value, 'checkbox')
              }
              value={defaultValues}
              renderInput={(params) => (
                <TextField {...params} placeholder={registeredFilter?.label} />
              )}
              componentsProps={{
                popper: {
                  disablePortal: true,
                },
              }}
              renderOption={(props, option, { selected }) => {
                const { ...optionProps } = props;
                return (
                  <li {...optionProps}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <span className="checkBoxLabel">{option.name}</span>
                  </li>
                );
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ width: '100%' }}
              disableCloseOnSelect={true}
            />
          )}
        </Grid>
      )}
      {registeredFilter?.type === 'DashboardDescriptorFilterDateRange' &&
        registeredFilter?.selectedValues && (
          <Box flexGrow={1}>
            <DateRangeComponent
              dashboardId={dashboardId}
              filterId={registeredFilter.filterId}
            />
          </Box>
        )}
    </Box>
  );
};

export default FilterComponent;
